import React from 'react';
import { TranslationField } from './TranslationField';
import { useCustomTexts } from '../../../../hooks/useCustomTexts';
import { ProductsMiniAppTranslation } from './ProductsMiniAppTranslation';

interface MiniAppTranslationContentProps {
  app: {
    id: string;
    label: string;
  };
  miniAppTranslation: {
    id: string;
    label: string;
    content?: {
      title?: string;
      description?: string;
      buttonText?: string;
      catalogTitle?: string;
      catalogSubtitle?: string;
      searchPlaceholder?: string;
      filterLabel?: string;
      sortLabel?: string;
      priceLabel?: string;
      stockLabel?: string;
      categoryLabel?: string;
      tagsLabel?: string;
      surfaceLabel?: string;
      roomsLabel?: string;
      locationLabel?: string;
      featuresLabel?: string;
      availableLabel?: string;
      soldLabel?: string;
      rentedLabel?: string;
      contactButtonLabel?: string;
      productDetails?: {
        [id: string]: {
          name: string;
          description: string;
          category?: string;
          tags?: string[];
        };
      };
      serviceDetails?: {
        [id: string]: {
          title: string;
          subtitle: string;
          content: string;
          buttonText: string;
        };
      };
      propertyDetails?: {
        [id: string]: {
          title: string;
          description: string;
          features: string[];
        };
      };
    };
  };
  language: {
    label: string;
  };
  products: any[];
  services: any[];
  properties: any[];
  onUpdate: (updates: any) => void;
}

export function MiniAppTranslationContent({ 
  app, 
  miniAppTranslation, 
  language, 
  products, 
  services, 
  properties,
  onUpdate 
}: MiniAppTranslationContentProps) {
  const { texts: currentTexts } = useCustomTexts();

  // Helper function to update nested content
  const updateNestedContent = (contentType: string, itemId: string, field: string, value: any) => {
    const currentContent = miniAppTranslation.content || {};
    const currentDetails = currentContent[contentType] || {};
    
    onUpdate({
      ...miniAppTranslation,
      content: {
        ...currentContent,
        [contentType]: {
          ...currentDetails,
          [itemId]: {
            ...(currentDetails[itemId] || {}),
            [field]: value
          }
        }
      }
    });
  };

  // Helper function to update direct content fields
  const updateContent = (field: string, value: string) => {
    onUpdate({
      ...miniAppTranslation,
      content: {
        ...(miniAppTranslation.content || {}),
        [field]: value
      }
    });
  };

  const renderProductsContent = () => (
    <ProductsMiniAppTranslation
      language={language}
      formData={miniAppTranslation.content || {}}
      onChange={(content) => onUpdate(content)}
          />
  );

  const renderCalendarContent = () => (
                <div>
      <p className="text-gray-300">Calendar translation settings will be added soon.</p>
    </div>
  );

  const renderServicesContent = () => (
    <div className="space-y-6">
      {/* Mini App Title */}
      <div>
        <TranslationField
          label="Mini-app title"
          field="label"
          value={miniAppTranslation.label}
          placeholder={`Title of the mini-app in ${language.label}`}
          originalValue={app.label}
          onChange={(field, value) => onUpdate({ ...miniAppTranslation, [field]: value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <TranslationField
          label="Title"
          field="title"
          value={miniAppTranslation.content?.title}
          placeholder={`Title in ${language.label}`}
          onChange={updateContent}
        />
        <TranslationField
          label="Description"
          field="description"
          value={miniAppTranslation.content?.description}
          placeholder={`Description in ${language.label}`}
          onChange={updateContent}
        />
      </div>

      <div className="border-t border-white/10 pt-6">
        <h4 className="text-lg font-medium text-white mb-4">Translation of services</h4>
        <div className="space-y-6">
          {services.map(service => (
            <div key={service.id} className="bg-white/5 rounded-lg p-4 space-y-4">
              <TranslationField
                label="Service title"
                field="title"
                value={miniAppTranslation.content?.serviceDetails?.[service.id]?.title}
                placeholder={`Title in ${language.label}`}
                originalValue={service.title}
                onChange={(field, value) => updateNestedContent('serviceDetails', service.id, field, value)}
              />
              <TranslationField
                label="Subtitle"
                field="subtitle"
                value={miniAppTranslation.content?.serviceDetails?.[service.id]?.subtitle}
                placeholder={`Subtitle in ${language.label}`}
                originalValue={service.subtitle || ''}
                onChange={(field, value) => updateNestedContent('serviceDetails', service.id, field, value)}
              />
              <TranslationField
                label="Content"
                field="content"
                value={miniAppTranslation.content?.serviceDetails?.[service.id]?.content}
                placeholder={`Content in ${language.label}`}
                originalValue={service.content}
                multiline
                onChange={(field, value) => updateNestedContent('serviceDetails', service.id, field, value)}
              />
              <TranslationField
                label="Button text"
                field="buttonText"
                value={miniAppTranslation.content?.serviceDetails?.[service.id]?.buttonText}
                placeholder={`Button text in ${language.label}`}
                originalValue={service.button_text || ''}
                onChange={(field, value) => updateNestedContent('serviceDetails', service.id, field, value)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderRealEstateContent = () => (
    <div className="space-y-6">
      {/* Mini App Title */}
      <div>
        <TranslationField
          label="Mini-app title"
          field="label"
          value={miniAppTranslation.label}
          placeholder={`Mini-app title in ${language.label}`}
          originalValue={app.label}
          onChange={(field, value) => onUpdate({ ...miniAppTranslation, [field]: value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-6">
        <TranslationField
          label="Catalog title"
          field="catalogTitle"
          value={miniAppTranslation.content?.catalogTitle}
          placeholder={`Catalog title in ${language.label}`}
          originalValue="Our properties"
          onChange={updateContent}
        />
        <TranslationField
          label="Catalog subtitle"
          field="catalogSubtitle"
          value={miniAppTranslation.content?.catalogSubtitle}
          placeholder={`Catalog subtitle in ${language.label}`}
          originalValue="Discover our available properties"
          onChange={updateContent}
        />
      </div>

      {/* Interface Labels */}
      <div className="border-t border-white/10 pt-6">
        <h4 className="text-lg font-medium text-white mb-4">Interface elements</h4>
        <div className="grid grid-cols-2 gap-6">
          <TranslationField
            label="Surface"
            field="surfaceLabel"
            value={miniAppTranslation.content?.surfaceLabel}
            placeholder={`Surface label in ${language.label}`}
            originalValue="Surface"
            onChange={updateContent}
          />
          <TranslationField
            label="Rooms"
            field="roomsLabel"
            value={miniAppTranslation.content?.roomsLabel}
            placeholder={`Rooms label in ${language.label}`}
            originalValue="Rooms"
            onChange={updateContent}
          />
          <TranslationField
            label="Location"
            field="locationLabel"
            value={miniAppTranslation.content?.locationLabel}
            placeholder={`Location label in ${language.label}`}
            originalValue="Localisation"
            onChange={updateContent}
          />
          <TranslationField
            label="Features"
            field="featuresLabel"
            value={miniAppTranslation.content?.featuresLabel}
            placeholder={`Features label in ${language.label}`}
            originalValue="Features"
            onChange={updateContent}
          />
        </div>
      </div>

      {/* Properties Translation */}
      <div className="border-t border-white/10 pt-6">
        <h4 className="text-lg font-medium text-white mb-4">Translation of properties</h4>
        <div className="space-y-6">
          {properties.map((property) => (
            <div key={property.id} className="bg-white/5 rounded-lg p-4 space-y-4">
              <TranslationField
                label="Property title"
                field="title"
                value={miniAppTranslation.content?.propertyDetails?.[property.id]?.title}
                placeholder={`Title in ${language.label}`}
                originalValue={property.title}
                onChange={(field, value) => updateNestedContent('propertyDetails', property.id, field, value)}
              />
              <TranslationField
                label="Property details"
                field="description"
                value={miniAppTranslation.content?.propertyDetails?.[property.id]?.description}
                placeholder={`Description in ${language.label}`}
                originalValue={property.description || ''}
                multiline
                onChange={(field, value) => updateNestedContent('propertyDetails', property.id, field, value)}
              />
              {/* Features translation */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Features
                </label>
                <div className="space-y-2">
                  {property.features?.map((feature, index) => (
                    <div key={index} className="grid grid-cols-2 gap-4">
                      <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-gray-400">
                        {feature}
                      </div>
                      <input
                        type="text"
                        value={miniAppTranslation.content?.propertyDetails?.[property.id]?.features?.[index] || ''}
                        onChange={(e) => {
                          const newFeatures = [...(miniAppTranslation.content?.propertyDetails?.[property.id]?.features || [])];
                          newFeatures[index] = e.target.value;
                          updateNestedContent('propertyDetails', property.id, 'features', newFeatures);
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder={`Caractéristique en ${language.label}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMiniAppContent = () => {
  switch (app.id) {
    case 'products':
      return renderProductsContent();
      case 'calendar':
        return renderCalendarContent();
    case 'services':
      return renderServicesContent();
    case 'realestate':
      return renderRealEstateContent();
    default:
      return (
          <div>
            <p className="text-gray-300">No translation settings available for this mini-app.</p>
        </div>
      );
  }
  };

  return renderMiniAppContent();
}